import { useContext, useMemo } from 'react';

import dayjs from 'dayjs';

import { useCluster } from '@cast/shared-cluster-context-data-access';
import { aggregateDataTransferDatapoints } from '@cast/split-me/features/cluster/cost-report';
import { useWorkloadDataTransferCostQuery } from '@cast/split-me/hooks';
import { WorkloadDetailsContext } from '@cast/split-me/features/cluster/workload-optimization';
import { fillGaps } from '@cast/split-me/utils';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { isRangeSupported } from '../../_utils/isRangeSupported';

const SECONDS_IN_DAY = 86400;

export const useInventoryWorkloadNetworkData = () => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { cluster } = useCluster();
  const { from, to } = useInventoryWorkloadsTimeRangeSelection();

  const fromParam = from ? dayjs.utc(from) : dayjs.utc(0);
  const toParam = to ? dayjs.utc(to) : dayjs.utc(0);
  const hasRange = !!from && !!to;
  const rangeSupported = hasRange && isRangeSupported(from, to);

  const { data, isLoading, error, refetch } = useWorkloadDataTransferCostQuery({
    clusterId: cluster.id,
    namespace: workload?.namespace || '',
    workload: workload?.name || '',
    workloadType: workload?.kind || '',
    startTime: fromParam.toISOString(),
    endTime: toParam.toISOString(),
    stepSeconds: SECONDS_IN_DAY,
    enabled:
      rangeSupported &&
      !!workload?.namespace &&
      !!workload?.kind &&
      !!workload?.name,
  });

  const chartData = useMemo(() => {
    if (!rangeSupported) {
      return [];
    }

    const timeSeriesData = aggregateDataTransferDatapoints(
      data?.item ? [data.item] : []
    );

    return fillGaps({
      timeSeries: timeSeriesData,
      dateRange: {
        from: fromParam,
        to: toParam,
      },
      filler: {},
      unit: 'day',
    });
  }, [data, fromParam, toParam]);

  return {
    data: chartData,
    isLoading,
    error,
    refetch,
    isRangeSupported: rangeSupported,
  };
};
